import { useState, useEffect, useCallback } from 'react'
import { Lock, Users, Clock, TrendingUp, ChevronDown, AlertCircle, Coffee, Sun } from 'lucide-react'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

const formatMinutes = (min: number) => {
  if (!min || min === 0) return '0h'
  const h = Math.floor(min / 60)
  const m = min % 60
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

const formatMinutesHMM = (min: number) => {
  const h = Math.floor(min / 60)
  const m = min % 60
  return `${h}:${String(m).padStart(2, '0')}`
}

const getWeekday = (dateStr: string) => {
  const [dd, mm, yy] = dateStr.split('.').map(Number)
  const fullYear = yy < 50 ? 2000 + yy : 1900 + yy
  const d = new Date(fullYear, mm - 1, dd)
  return ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'][d.getDay()]
}

interface DayData {
  date: string; kommen?: string; gehen?: string; presentMinutes: number
  breakMinutes: number; nettoMinutes: number; overtimeMinutes: number
  isSaturday?: boolean; isSunday?: boolean
}

interface EmployeeData {
  employee: { name: string; type: string }
  summary: { weekdayCount: number; weekendCount: number; totalNettoMinutes: number; totalOvertimeMinutes: number; totalOvertimeFormatted: string }
  days: DayData[]
}

function PasswordGate({ onAuth }: { onAuth: (token: string) => void }) {
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setLoading(true); setError('')
    try {
      const res = await fetch(`${API_BASE}/uebersicht/auth`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ password }) })
      if (res.status === 401) { setError('Bitte zuerst in der WebUI anmelden'); return }
      if (!res.ok) { setError('Falsches Passwort'); return }
      const data = await res.json()
      onAuth(data.token)
    } catch { setError('Verbindungsfehler') }
    finally { setLoading(false) }
  }

  return (
    <div className="flex min-h-[60vh] items-center justify-center bg-gray-50 p-4">
      <form onSubmit={handleSubmit} className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-lg">
        <div className="mb-6 flex justify-center"><div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-50"><Lock className="h-8 w-8 text-blue-600" /></div></div>
        <h1 className="mb-2 text-center text-xl font-bold text-gray-800">Mitarbeiter-Uebersicht</h1>
        <p className="mb-6 text-center text-sm text-gray-500">Bitte Passwort eingeben</p>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Passwort" className="mb-3 w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500" autoFocus />
        {error && <div className="mb-3 flex items-center gap-2 text-sm text-red-600"><AlertCircle className="h-4 w-4 shrink-0" />{error}</div>}
        <button type="submit" disabled={loading || !password} className="w-full rounded-xl bg-blue-600 py-3 font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50">{loading ? 'Pruefe...' : 'Anmelden'}</button>
      </form>
    </div>
  )
}

export default function MitarbeiterUebersicht() {
  const [token, setToken] = useState<string | null>(() => {
    const saved = sessionStorage.getItem('uebersicht_token')
    const expiry = sessionStorage.getItem('uebersicht_expiry')
    if (saved && expiry && Date.now() < parseInt(expiry)) return saved
    return null
  })

  const [employees, setEmployees] = useState<{ userId: number; name: string; employmentType?: string }[]>([])
  const [selectedEmployee, setSelectedEmployee] = useState('')
  const [month, setMonth] = useState(new Date().getMonth() + 1)
  const [year, setYear] = useState(new Date().getFullYear())
  const [data, setData] = useState<EmployeeData | null>(null)
  const [loading, setLoading] = useState(false)
  const [multiplier, setMultiplier] = useState('')

  const handleAuth = (newToken: string) => {
    setToken(newToken)
    sessionStorage.setItem('uebersicht_token', newToken)
    sessionStorage.setItem('uebersicht_expiry', String(Date.now() + 3600000))
  }

  useEffect(() => {
    if (!token) return
    fetch(`${API_BASE}/uebersicht/mitarbeiter`, { headers: { 'x-uebersicht-token': token }, credentials: 'include' })
      .then(r => { if (r.status === 401) { setToken(null); return null } return r.json() })
      .then(d => d && setEmployees(d)).catch(() => {})
  }, [token])

  const loadData = useCallback(async () => {
    if (!token || !selectedEmployee) { setData(null); return }
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/uebersicht/zeiten?userId=${selectedEmployee}&month=${month}&year=${year}`, { headers: { 'x-uebersicht-token': token }, credentials: 'include' })
      if (res.status === 401) { setToken(null); return }
      setData(await res.json())
    } catch { setData(null) }
    finally { setLoading(false) }
  }, [token, selectedEmployee, month, year])

  useEffect(() => { loadData() }, [loadData])

  if (!token) return <PasswordGate onAuth={handleAuth} />

  const months = ['Januar','Februar','Maerz','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember']

  return (
    <div className="pb-40">
      <div className="sticky top-0 z-10 border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-4xl px-3 py-3 sm:px-4 sm:py-4">
          <div className="mb-3 flex items-center gap-3 sm:mb-4"><Users className="h-5 w-5 text-blue-600 sm:h-6 sm:w-6" /><h1 className="text-base font-bold text-gray-800 sm:text-lg">Mitarbeiter-Uebersicht</h1></div>
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
            <div className="relative flex-1">
              <select value={selectedEmployee} onChange={(e) => setSelectedEmployee(e.target.value)} className="w-full appearance-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 pr-10 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500 sm:px-4">
                <option value="">Mitarbeiter wählen...</option>
                {employees.map(e => <option key={e.userId} value={e.userId}>{e.name} ({e.employmentType || 'VZ'})</option>)}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-3 h-4 w-4 text-gray-400" />
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1 sm:flex-none">
                <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))} className="w-full appearance-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 pr-10 text-sm outline-none focus:ring-2 focus:ring-blue-500 sm:w-auto sm:px-4">
                  {months.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-3 h-4 w-4 text-gray-400" />
              </div>
              <div className="relative flex-1 sm:flex-none">
                <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="w-full appearance-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 pr-10 text-sm outline-none focus:ring-2 focus:ring-blue-500 sm:w-auto sm:px-4">
                  {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-3 h-4 w-4 text-gray-400" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-3 py-4 sm:px-4 sm:py-6">
        {!selectedEmployee && <div className="py-16 text-center text-gray-400"><Users className="mx-auto mb-3 h-12 w-12 opacity-50" /><p>Bitte Mitarbeiter auswählen</p></div>}
        {loading && <div className="py-16 text-center"><div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" /><p className="text-sm text-gray-500">Lade Daten...</p></div>}

        {data && !loading && (<>
          <div className="mb-4 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="text-base font-bold text-gray-800 sm:text-lg">{data.employee.name}</h2>
                <span className={`mt-1 inline-block rounded-full px-3 py-0.5 text-xs font-medium ${data.employee.type === 'TZ' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>{data.employee.type === 'TZ' ? 'Teilzeit (4h/Tag)' : 'Vollzeit (8h/Tag, 1h Pause)'}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2"><label className="text-xs text-gray-500">Faktor:</label><input type="number" step="0.01" min="0" placeholder="z.B. 25" value={multiplier} onChange={(e) => setMultiplier(e.target.value)} className="w-20 rounded-lg border border-gray-200 px-2 py-1 text-right text-base outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500" /></div>
                <div className="text-right"><p className="text-sm text-gray-500">{months[month - 1]} {year}</p><p className="text-xs text-gray-400">{data.summary.weekdayCount} Werktage{data.summary.weekendCount > 0 && ` + ${data.summary.weekendCount} Sa/So`}</p></div>
              </div>
            </div>
          </div>

          <div className="mb-4 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
            {data.days.length === 0 && <div className="py-8 text-center text-gray-400">Keine Eintraege</div>}
            {/* Desktop */}
            <div className="hidden divide-y divide-gray-50 sm:block">
              {data.days.map((day, i) => { const wd = getWeekday(day.date); const isWe = day.isSaturday || day.isSunday; return (
                <div key={i} className={`flex items-center gap-4 px-5 py-3 ${isWe ? 'bg-amber-50/60' : ''}`}>
                  <div className="w-20 shrink-0"><span className={`text-sm font-medium ${isWe ? 'text-amber-700' : 'text-gray-800'}`}>{wd} {day.date.substring(0, 5)}</span></div>
                  <div className="flex w-32 shrink-0 items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-gray-400" /><span className="text-sm text-gray-600">{day.kommen || '--:--'} – {day.gehen || '--:--'}</span></div>
                  <div className="w-16 shrink-0 text-sm text-gray-400">{formatMinutes(day.presentMinutes)}</div>
                  {day.breakMinutes > 0 ? <div className="flex w-14 shrink-0 items-center gap-1"><Coffee className="h-3 w-3 text-gray-300" /><span className="text-xs text-gray-400">-{day.breakMinutes}m</span></div> : <div className="w-14 shrink-0" />}
                  <div className="w-14 shrink-0 text-sm font-medium text-gray-700">= {formatMinutes(day.nettoMinutes)}</div>
                  <div className="flex-1 text-right">{day.overtimeMinutes > 0 ? <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${isWe ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{isWe ? <Sun className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}+{formatMinutes(day.overtimeMinutes)}</span> : <span className="text-xs text-gray-300">—</span>}</div>
                </div>
              )})}
            </div>
            {/* Mobile */}
            <div className="divide-y divide-gray-50 sm:hidden">
              {data.days.map((day, i) => { const wd = getWeekday(day.date); const isWe = day.isSaturday || day.isSunday; return (
                <div key={i} className={`px-4 py-3 ${isWe ? 'bg-amber-50/60' : ''}`}>
                  <div className="mb-1 flex items-center justify-between">
                    <span className={`text-sm font-medium ${isWe ? 'text-amber-700' : 'text-gray-800'}`}>{wd} {day.date.substring(0, 5)}</span>
                    {day.overtimeMinutes > 0 ? <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${isWe ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>+{formatMinutes(day.overtimeMinutes)}</span> : <span className="text-xs text-gray-300">—</span>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>{day.kommen || '--:--'} – {day.gehen || '--:--'}</span><span>·</span><span>{formatMinutes(day.presentMinutes)}</span>
                    {day.breakMinutes > 0 && <><span>·</span><span className="text-gray-400">-{day.breakMinutes}m</span></>}
                    <span>·</span><span className="font-medium text-gray-700">= {formatMinutes(day.nettoMinutes)}</span>
                  </div>
                </div>
              )})}
            </div>
          </div>
        </>)}
      </div>

      {data && !loading && data.days.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-20">
          <div className="mx-auto max-w-4xl px-3 pb-3 sm:px-4 sm:pb-4">
            <div className="rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700 p-4 text-white shadow-xl sm:p-5">
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <div><p className="text-[10px] uppercase tracking-wide text-blue-200 sm:text-xs">Arbeitszeit</p><p className="text-lg font-bold sm:text-2xl">{formatMinutesHMM(data.summary.totalNettoMinutes)}</p><p className="text-[10px] text-blue-200 sm:text-xs">nach Pause</p></div>
                <div><p className="text-[10px] uppercase tracking-wide text-blue-200 sm:text-xs">Ueberstunden</p><p className="text-lg font-bold text-green-300 sm:text-2xl">+{data.summary.totalOvertimeFormatted}</p><p className="text-[10px] text-blue-200 sm:text-xs">gesamt</p></div>
                <div><p className="text-[10px] uppercase tracking-wide text-yellow-200 sm:text-xs">Multiplikator</p>
                  {multiplier && parseFloat(multiplier) > 0 ? (<><p className="text-lg font-bold text-yellow-300 sm:text-2xl">{(data.summary.totalOvertimeMinutes / 60 * parseFloat(multiplier)).toFixed(2)}€</p><p className="text-[10px] text-blue-200 sm:text-xs">{formatMinutesHMM(data.summary.totalOvertimeMinutes)}h × {multiplier}€</p></>) : (<><p className="text-lg font-bold text-blue-300/50 sm:text-2xl">—</p><p className="text-[10px] text-blue-200 sm:text-xs">Faktor eingeben</p></>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
